<template>
  <div class="z-0 size-full bg-gpx_gray-700" id="map" ref="mapContainer" />
</template>

<script setup lang="ts">
import * as _ from 'lodash';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import {
  toRaw,
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import * as shapeUtils from './utils/shape';
import {
  createStopShape,
  createStopTooltip,
  sortedStops,
} from './utils/stop-shape';
import * as markerUtils from './utils/marker';
import * as legendUtils from './utils/legend';
import * as geojsonUtils from './utils/geo-json';
import * as arcUtils from './utils/arc';
import * as layerUtils from '@/utils/map/layer';
import { clusterOptions } from '@/components/device/map/utils/marker';
import { mapCenter } from '@/config/constants';
import { useMapResize } from '@/utils/map/resize';
import { useAiActions } from '@/utils/map/aiActions.ts';
import {
  addOverlayIfEnabled,
  createOverlayPane,
  renderMapOverlays,
} from '@/utils/map/overlay';
import { getAccountMapOverlays } from '@gpx-spa/queries';
import { decodeShipmentTrackRow } from '@/utils/shipment-detail-map-decode';
import { buildShipmentTrackDisplay } from '@/map/operational/track-display/build-shipment-track-display';
import { shipmentTrackVertexPayload } from '@/map/operational/shipment-track-vertex-payload';
import { formatTrackVertexPopup } from '@/utils/track-vertex-popup';
import {
  createLeafletCanvasPointCloudLayer,
  ensureLeafletTrackVertexPane,
} from '@gpx-spa/geo-adapters';

// Props
const props = defineProps({
  view: {
    type: String as () => string,
    required: true,
  },
});

// Store, Router and Route
const store = useStore();
const router = useRouter();
const route = useRoute();

// Reactive state (replaces data())
const dbClickTimer = ref(null);
const center = ref(mapCenter);
const map = ref(null);
const mainLayers = ref([]);
const overlays = ref({});
const layerControl = ref(null);
const marker = ref(null);
const markers = ref(null);
const shapes = ref(L.featureGroup());
const paths = ref(L.featureGroup());
const pathPoints = ref(L.featureGroup());
const trackPointCloudLayer = ref(null);
const fullTrackVertices = ref([]);
const arcs = ref(L.featureGroup());
const cleanupResizeObserver = ref(null);
const unsubscribeFromAiActions = ref(null);
const mapContainer = ref(null);
const mapOverlayGroup = L.layerGroup([]);

// Computed properties (replaces computed)
const accountId = computed(() => store.getters['auth/accountId']);
const { data: mapOverlaysData } = getAccountMapOverlays(accountId);
const canUseBleFeature = computed(() => store.getters['auth/canUseBleFeature']);
const shipments = computed(() => store.getters['shipment/filtered']);
const shipmentById = (id) => store.getters['shipment/byId'](id);
const publicShipment = computed(() => store.getters['shipment/publicShipment']);
const uuid = computed(() => route.params.uuid);
const shipment = computed(() => {
  if (uuid.value) {
    return publicShipment.value;
  }
  const shipmentId = +route.params.id;
  return shipmentById(shipmentId);
});

// Watchers (replaces watch)
watch(
  [() => map.value, () => mapOverlaysData.value],
  ([mapInstance, overlaysData]) => {
    if (!mapInstance) return;
    renderMapOverlays(toRaw(mapInstance), overlaysData || [], mapOverlayGroup);
  },
  { immediate: true },
);

watch(
  () => props.view,
  (newView, oldView) => {
    setActiveLayer(newView, oldView);
  },
  { immediate: true },
);

watch(
  () => shipments.value,
  (n, o) => {
    if (props.view === 'cluster') {
      if (_.isEqual(n, o)) return;
      updatePositionsShapesTracks();
    }
  },
  { deep: true },
);

// Lifecycle hooks (replaces mounted and beforeUnmount)
onMounted(() => {
  setupLeafletMap();
  setupLegend();
  setActiveLayer(props.view);

  nextTick(() => {
    cleanupResizeObserver.value = useMapResize(map, mapContainer, { delay: 0 });
    unsubscribeFromAiActions.value = useAiActions(map);
  });
});

onBeforeUnmount(() => {
  removeTrackPointCloudLayer();
  if (cleanupResizeObserver.value) {
    cleanupResizeObserver.value();
    cleanupResizeObserver.value = null;
  }

  if (unsubscribeFromAiActions.value) {
    unsubscribeFromAiActions.value();
    unsubscribeFromAiActions.value = null;
  }
});

// Methods (rewritten as functions)
function setupLeafletMap() {
  const layers = layerUtils.getLayers();
  const { id } = layerUtils.useLocalStorageLayer();
  mainLayers.value = layers;

  const overlaysObj = {
    'Show start/end': toRaw(shapes.value),
    'Show tracks': toRaw(paths.value),
    'Show overlays': mapOverlayGroup,
  };
  overlays.value = overlaysObj;

  map.value = L.map('map', {
    layers: [layers[id]],
    center: toRaw(center.value),
    zoomControl: false,
    zoom: 5,
    minZoom: 1,
    preferCanvas: true,
  });

  L.control.zoom({ position: 'topright' }).addTo(toRaw(map.value));
  layerControl.value = L.control
    .layers(toRaw(mainLayers.value), toRaw(overlays.value), {
      position: 'topleft',
    })
    .addTo(toRaw(map.value));

  createOverlayPane(toRaw(map.value));

  addOverlayIfEnabled(toRaw(map.value), mapOverlayGroup, 'mapOverlays');

  listenZoom();
  listenOverLayChange();
  listenLayerChange();
}

function isDevicesPresentation(s) {
  return Array.isArray(s?.device_ids);
}

function deviceById(s, deviceId) {
  return (
    (s?.devices ?? []).find((d) => Number(d.id) === Number(deviceId)) ?? null
  );
}

function setActiveLayer(newView, oldView) {
  if (newView === 'cluster') {
    if (oldView === 'single') {
      toRaw(shapes.value).clearLayers();
      if (marker.value) {
        toRaw(map.value).removeLayer(toRaw(marker.value));
        marker.value = null;
      }
      if (markers.value) {
        toRaw(map.value).removeLayer(toRaw(markers.value));
        markers.value = null;
      }
      toRaw(paths.value).clearLayers();
      toRaw(pathPoints.value).clearLayers();
      removeTrackPointCloudLayer();
      toRaw(arcs.value).clearLayers();
    }
    setupCluster();
    setupClusterStartEnd();
    setupClustePath();
    setupClusterArcs();
    setClusterView();
  }
  if (newView === 'single') {
    if (oldView === 'cluster') {
      toRaw(shapes.value).clearLayers();
      if (markers.value) {
        toRaw(markers.value).clearLayers();
        toRaw(map.value).removeLayer(toRaw(markers.value));
      }
      toRaw(paths.value).clearLayers();
      toRaw(arcs.value).clearLayers();
    }
    setupSingle();
    setupSingleStartEnd();
    setupSinglePath();
    setupSingleArc();
    setSingleView();
  }
}

function setupLegend() {
  if (uuid.value) return;
  const legend = legendUtils.createLegend();
  legend.addTo(map.value);
}

function setupCluster() {
  markers.value = L.markerClusterGroup(clusterOptions);
  shipments.value.forEach((shipment) => createClusterMarker(shipment));
}

function setupSingle() {
  const s = shipment.value;
  if (!s) return;
  if (isDevicesPresentation(s)) {
    createDevicesModeMarkers(s);
    return;
  }
  createSingleMarker({ shipment: s });
}

function setupClusterStartEnd() {
  shipments.value.forEach((shipment) => createClusterShape({ shipment }));
}

function setupSingleStartEnd() {
  const s = shipment.value;
  if (!s) return;
  createSingleShape({ shipment: s });
}

function setupClustePath() {
  const geoJsonLines = shipments.value
    .filter((shipment) => Boolean(shipment.path))
    .map((shipment) => geojsonUtils.convertShipmentToGeoJson({ shipment }));
  geoJsonLines.forEach((l) => toRaw(l).addTo(toRaw(paths.value)));
}

function removeTrackPointCloudLayer() {
  const mapInstance = toRaw(map.value);
  const layer = trackPointCloudLayer.value;
  if (mapInstance && layer && mapInstance.hasLayer(toRaw(layer))) {
    mapInstance.removeLayer(toRaw(layer));
  }
  trackPointCloudLayer.value = null;
  fullTrackVertices.value = [];
}

function setupDevicesTrackPointCloud() {
  const mapInstance = toRaw(map.value);
  if (!mapInstance) return;
  ensureLeafletTrackVertexPane(mapInstance);
  const points = [...fullTrackVertices.value];
  const prev = trackPointCloudLayer.value;
  if (prev && mapInstance.hasLayer(toRaw(prev))) {
    mapInstance.removeLayer(toRaw(prev));
  }
  trackPointCloudLayer.value = null;
  if (points.length === 0) return;
  fullTrackVertices.value = points;

  trackPointCloudLayer.value = createLeafletCanvasPointCloudLayer({
    layerId: 'public-shipment-track-vertices',
    onPointClick(point, latlng) {
      const payload = shipmentTrackVertexPayload(point.payload);
      if (!payload) return;
      L.popup({ closeButton: true, closeOnClick: true })
        .setLatLng(latlng)
        .setContent(formatTrackVertexPopup(payload))
        .openOn(mapInstance);
    },
  });
  toRaw(trackPointCloudLayer.value).setPointCloud(
    { points },
    { thinning: { strategy: 'minGap', mode: 'auto' } },
  );
  const { is: isPaths } = layerUtils.useLocalStorageOverlay('paths');
  if (isPaths) {
    toRaw(trackPointCloudLayer.value).addTo(mapInstance);
  }
}

function setupSinglePath() {
  const s = shipment.value;
  if (!s) return;

  if (isDevicesPresentation(s) && Array.isArray(s.tracks)) {
    const decodedRows = [];
    s.tracks.forEach((row) => {
      const decoded = decodeShipmentTrackRow(row);
      if (!decoded?.coordinates?.length) return;
      decodedRows.push({ device_id: row.device_id, decoded });
    });
    const display = buildShipmentTrackDisplay(decodedRows, s.status);
    for (const feature of display.lineCollection.features) {
      if (feature.geometry?.type !== 'LineString') continue;
      const color =
        feature.properties?.map?.lineColor ?? '#64748b';
      const line = geojsonUtils.convertCoordinatesToGeoJsonLine({
        coordinates: feature.geometry.coordinates,
        color,
      });
      toRaw(line).addTo(toRaw(paths.value));
    }
    fullTrackVertices.value = display.pointCloud.points;
    setupDevicesTrackPointCloud();
    return;
  }

  if (!s.path) return;
  const geoJsonLine = geojsonUtils.convertShipmentToGeoJson({
    shipment: s,
  });
  const featureCollection = geojsonUtils.convertToPositionPointFC({
    path: s.path,
  });
  pathPoints.value = L.geoJSON(featureCollection, {
    onEachFeature: geojsonUtils.onEachPositionPointFeature,
    pointToLayer: (point, latlng) => geojsonUtils.createPosition(point, latlng),
  });
  toRaw(geoJsonLine).addTo(toRaw(paths.value));
}

function setupClusterArcs() {
  shipments.value.forEach((s) => {
    const arc = arcUtils.createArc(s);
    if (arc) arc.addTo(toRaw(arcs.value));
  });
}

function setupSingleArc() {
  if (!uuid.value) return;
  const s = shipment.value;
  if (!s) return;
  const arc = arcUtils.createArc(s);
  if (arc) arc.addTo(toRaw(arcs.value));
}

function setClusterView() {
  const { is: isPaths } = layerUtils.useLocalStorageOverlay('paths');
  const { is: isStartEnd } = layerUtils.useLocalStorageOverlay('startend');

  if (_.isEmpty(shipments.value)) return;

  toRaw(map.value).addLayer(toRaw(markers.value));
  toRaw(arcs.value).addTo(toRaw(map.value));
  let bounds = toRaw(markers.value).getBounds();
  if (isPaths) {
    toRaw(paths.value).addTo(toRaw(map.value));
  }
  if (isStartEnd) {
    toRaw(shapes.value).addTo(toRaw(map.value));
    bounds = toRaw(shapes.value).getBounds();
  }

  toRaw(map.value).fitBounds(bounds, { maxZoom: 12 });
}

function setSingleView() {
  const { is: isPaths } = layerUtils.useLocalStorageOverlay('paths');
  const { is: isStartEnd } = layerUtils.useLocalStorageOverlay('startend');

  if (marker.value) {
    toRaw(map.value).addLayer(toRaw(marker.value));
  }
  if (
    markers.value &&
    markers.value.getLayers &&
    markers.value.getLayers().length
  ) {
    toRaw(map.value).addLayer(toRaw(markers.value));
  }
  if (uuid.value && !_.isEmpty(arcs.value._layers)) {
    toRaw(arcs.value).addTo(toRaw(map.value));
  }
  let bounds;
  if (isPaths) {
    toRaw(paths.value).addTo(toRaw(map.value));
    if (!_.isEmpty(paths.value._layers)) {
      bounds = toRaw(paths.value).getBounds();
    }
  }
  if (
    markers.value &&
    markers.value.getLayers &&
    markers.value.getLayers().length
  ) {
    const markerBounds = toRaw(markers.value).getBounds();
    bounds = bounds ? bounds.extend(markerBounds) : markerBounds;
  }
  if (isStartEnd) {
    toRaw(shapes.value).addTo(toRaw(map.value));
    if (!_.isEmpty(shapes.value._layers)) {
      const shapeBounds = toRaw(shapes.value).getBounds();
      bounds = bounds ? bounds.extend(shapeBounds) : shapeBounds;
    }
  }
  if (!bounds && uuid.value && !_.isEmpty(arcs.value._layers)) {
    bounds = toRaw(arcs.value).getBounds();
  }

  if (bounds && bounds.isValid && bounds.isValid()) {
    toRaw(map.value).fitBounds(bounds, {
      maxZoom: 12,
    });
  } else if (marker.value) {
    toRaw(map.value).setView(toRaw(marker.value)._latlng, 15);
  }
}

function createClusterMarker(shipment) {
  const getShipment = () => shipmentById(shipment.id);
  if (!shipment.current_position) return;
  const content = markerUtils.getPopupContent({ shipment });
  const markerInstance = markerUtils.createMarker(shipment, {
    isNewIcon: canUseBleFeature.value,
  });

  markerInstance
    .bindPopup(content, {
      closeButton: false,
      minWidth: 200,
    })
    .openPopup()
    .on('mouseover', function () {
      const shipmentData = getShipment();
      const content = markerUtils.getPopupContent({ shipment: shipmentData });
      this.getPopup().setContent(content);
      this.openPopup();
    })
    .on('click', (event) => {
      if (dbClickTimer.value !== null) {
        return;
      }
      dbClickTimer.value = setTimeout(() => {
        dbClickTimer.value = null;
        handleClusterMarkerClick({ event, shipment });
      }, 200);
    })
    .on('dblclick', () => {
      clearTimeout(dbClickTimer.value);
      dbClickTimer.value = null;
    });

  toRaw(markers.value).addLayer(markerInstance);
}

function createSingleMarker({ shipment }) {
  if (!shipment.current_position) return;
  const content = markerUtils.getPopupContent({ shipment });

  marker.value = markerUtils.createMarker(shipment, {
    isNewIcon: canUseBleFeature.value,
  });

  marker.value.bindPopup(content, {
    closeButton: false,
    minWidth: 200,
  });
}

/**
 * Latest-ping pins sit above the track-vertex canvas (z-index 655), which otherwise
 * swallows clicks on the pin and opens the vertex popup (no address) instead.
 */
const DEVICE_POSITION_MARKER_PANE = 'gpxShipmentDevicePositionMarkers';

/** Returns the pane name, or undefined when the map is not ready yet. */
function ensureDevicePositionMarkerPane() {
  const mapInstance = toRaw(map.value);
  if (!mapInstance) return undefined;
  if (!mapInstance.getPane(DEVICE_POSITION_MARKER_PANE)) {
    const pane = mapInstance.createPane(DEVICE_POSITION_MARKER_PANE);
    pane.style.zIndex = '670';
  }
  return DEVICE_POSITION_MARKER_PANE;
}

function createDevicesModeMarkers(s) {
  const positions = Array.isArray(s.current_positions)
    ? s.current_positions
    : [];
  if (!positions.length) return;

  const pane = ensureDevicePositionMarkerPane();
  markers.value = L.featureGroup();
  positions.forEach((position) => {
    if (!position?.location) return;
    const device = deviceById(s, position.device_id);
    const content = markerUtils.getDevicePositionPopupContent({
      position,
      device,
    });
    const m = markerUtils.createDevicePositionMarker(s, position, {
      isNewIcon: canUseBleFeature.value,
      pane,
    });
    if (!m) return;
    m.bindPopup(content, {
      closeButton: true,
      minWidth: 200,
    });
    toRaw(markers.value).addLayer(m);
  });
}

function createClusterShape({ shipment }) {
  const fromShape = shapeUtils.createShape({
    geofenceId: shipment.from_geofence_id,
    geofence: shipment.from_geofence,
    address: shipment.from_address,
  });
  const toShape = shapeUtils.createShape({
    geofenceId: shipment.to_geofence_id,
    geofence: shipment.to_geofence,
    address: shipment.to_address,
  });

  const fromTooltip = shapeUtils.createTooltip(
    {
      geofenceId: shipment.from_geofence_id,
      geofence: shipment.from_geofence,
      address: shipment.from_address,
    },
    'from',
  );
  const toTooltip = shapeUtils.createTooltip(
    {
      geofenceId: shipment.to_geofence_id,
      geofence: shipment.to_geofence,
      address: shipment.to_address,
    },
    'to',
  );
  fromShape.bindTooltip(fromTooltip);
  toShape.bindTooltip(toTooltip);
  fromShape.addTo(toRaw(shapes.value));
  toShape.addTo(toRaw(shapes.value));
}

function drawStopShapes(shipment) {
  const stops = sortedStops(shipment.stops);
  stops.forEach((stop, index) => {
    const layer = createStopShape(stop);
    if (!layer) return;
    const direction =
      index === 0 ? 'from' : index === stops.length - 1 ? 'to' : 'stop';
    const tooltip = createStopTooltip(stop, direction);
    if (tooltip) {
      layer.bindTooltip(tooltip);
    }
    layer.addTo(toRaw(shapes.value));
  });
}

function createSingleShape({ shipment }) {
  if (!shipment) return;

  // Public share page only ever has nested stops (no from_/to_ geofence fields).
  if (uuid.value) {
    drawStopShapes(shipment);
    return;
  }

  const stops = sortedStops(shipment.stops);
  if (stops.length) {
    drawStopShapes(shipment);
    return;
  }

  const fromShape = shapeUtils.createShape({
    geofenceId: shipment.from_geofence_id,
    geofence: shipment.from_geofence,
    address: shipment.from_address,
  });
  const toShape = shapeUtils.createShape({
    geofenceId: shipment.to_geofence_id,
    geofence: shipment.to_geofence,
    address: shipment.to_address,
  });

  const fromTooltip = shapeUtils.createTooltip(
    {
      geofenceId: shipment.from_geofence_id,
      geofence: shipment.from_geofence,
      address: shipment.from_address,
    },
    'from',
  );
  const toTooltip = shapeUtils.createTooltip(
    {
      geofenceId: shipment.to_geofence_id,
      geofence: shipment.to_geofence,
      address: shipment.to_address,
    },
    'to',
  );
  fromShape.bindTooltip(fromTooltip);
  toShape.bindTooltip(toTooltip);
  fromShape.addTo(toRaw(shapes.value));
  toShape.addTo(toRaw(shapes.value));
}

function handleClusterMarkerClick({ event }) {
  router.push({
    name: 'ShipmentDetailsView',
    params: {
      id: event.target.options.id,
    },
  });
}

function updatePositionsShapesTracks() {
  toRaw(markers.value).clearLayers();
  toRaw(shapes.value).clearLayers();
  toRaw(paths.value).clearLayers();
  toRaw(arcs.value).clearLayers();
  setupCluster();
  setupClusterStartEnd();
  setupClustePath();
  setupClusterArcs();
  setClusterView();
}

function managePathsPoints() {
  if (toRaw(map.value).getZoom() >= 6) {
    toRaw(pathPoints.value).addTo(toRaw(paths.value));
  } else {
    toRaw(paths.value).removeLayer(pathPoints.value);
  }
}

function syncTrackPointCloudOverlayVisibility() {
  const mapInstance = toRaw(map.value);
  const layer = trackPointCloudLayer.value;
  if (!mapInstance || !layer) return;
  const { is: isPaths } = layerUtils.useLocalStorageOverlay('paths');
  const onMap = mapInstance.hasLayer(toRaw(layer));
  if (isPaths && !onMap) {
    toRaw(layer).addTo(mapInstance);
  } else if (!isPaths && onMap) {
    mapInstance.removeLayer(toRaw(layer));
  }
}

function listenZoom() {
  toRaw(map.value).on('zoomend', () => {
    if (pathPoints.value) {
      managePathsPoints();
    }
  });
}

function listenLayerChange() {
  const { set } = layerUtils.useLocalStorageLayer();
  toRaw(map.value).on('baselayerchange', (e) => {
    set(e.name);
  });
}

function listenOverLayChange() {
  const { set: setStartEndValue } =
    layerUtils.useLocalStorageOverlay('startend');
  const { set: setPathsValue } = layerUtils.useLocalStorageOverlay('paths');
  const { set: setMapOverlaysValue } =
    layerUtils.useLocalStorageOverlay('mapOverlays');

  toRaw(map.value).on('overlayadd', (e) => {
    if (e.name === 'Show start/end') {
      setStartEndValue(true);
    }
    if (e.name === 'Show tracks') {
      setPathsValue(true);
      syncTrackPointCloudOverlayVisibility();
    }
    if (e.name === 'Show overlays') {
      setMapOverlaysValue(true);
    }
  });
  toRaw(map.value).on('overlayremove', (e) => {
    if (e.name === 'Show start/end') {
      setStartEndValue(false);
    }
    if (e.name === 'Show tracks') {
      setPathsValue(false);
      syncTrackPointCloudOverlayVisibility();
    }
    if (e.name === 'Show overlays') {
      setMapOverlaysValue(false);
    }
  });
}
</script>
